import { useState } from 'react';
import { CriteriaOperatorEnum } from '../../enums/criteriaOperator.enum';
import {
  CatalogCreationCriterionEnum,
  CatalogCreationCriterionOption,
  CatalogCriterionChange,
  UseCatalogCriteriaEditorParams,
} from '../catalogCreationCriteria.types';
import {
  buildCatalogCreationCriterionChips,
  findCatalogCreationCriterionConfig,
  toCatalogCriterionAvailabilities,
  toggleCatalogCreationCriterionValue,
} from '../catalogCreationCriteria.utils';

export const useCatalogCriteriaEditor = ({
  values,
  operators,
  errorMessage,
  onRegisterOptionLabels,
  onChangeCriterion,
}: UseCatalogCriteriaEditorParams) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeCriterion, setActiveCriterion] = useState<CatalogCreationCriterionEnum | null>(null);
  const [draftValues, setDraftValues] = useState<string[]>([]);
  const [draftOperator, setDraftOperator] = useState<CriteriaOperatorEnum>(CriteriaOperatorEnum.OR);

  const chips = buildCatalogCreationCriterionChips({ values, operators });

  const changeCriterion = ({ criterion, values: nextValues, operator }: CatalogCriterionChange) => {
    const isAvailability = criterion === CatalogCreationCriterionEnum.AVAILABILITY;

    onChangeCriterion({
      criterion,
      operator,
      values: isAvailability ? toCatalogCriterionAvailabilities(nextValues) : nextValues,
    });
  };

  const startCriterionDraft = (criterion: CatalogCreationCriterionEnum) => {
    const { valuesField, operatorField } = findCatalogCreationCriterionConfig(criterion);

    setDraftValues([...values[valuesField]]);
    setDraftOperator(operatorField ? operators[operatorField] : CriteriaOperatorEnum.OR);
    setActiveCriterion(criterion);
  };

  const openCriteriaTypes = (element: HTMLElement) => {
    setActiveCriterion(null);
    setAnchorEl(element);
  };

  const openCriterion = (criterion: CatalogCreationCriterionEnum, element: HTMLElement) => {
    startCriterionDraft(criterion);
    setAnchorEl(element);
  };

  const closeCriteria = () => {
    setAnchorEl(null);
    setActiveCriterion(null);
  };

  const backToCriteriaTypes = () => {
    setActiveCriterion(null);
  };

  const toggleDraftOption = (option: CatalogCreationCriterionOption) => {
    setDraftValues((currentValues) =>
      toggleCatalogCreationCriterionValue({ values: currentValues, optionId: option.id }),
    );
    onRegisterOptionLabels({
      [option.id]: {
        name: option.name,
        description: option.description,
        groupId: option.groupId,
      },
    });
  };

  const applyDraft = () => {
    if (!activeCriterion) {
      return;
    }

    changeCriterion({ criterion: activeCriterion, values: draftValues, operator: draftOperator });
    closeCriteria();
  };

  const removeCriterion = (criterion: CatalogCreationCriterionEnum) => {
    changeCriterion({ criterion, values: [], operator: CriteriaOperatorEnum.OR });
  };

  const activeOperatorField = activeCriterion
    ? findCatalogCreationCriterionConfig(activeCriterion).operatorField
    : null;

  return {
    chips,
    anchorEl,
    activeCriterion,
    draftValues,
    draftOperator: activeOperatorField ? draftOperator : null,
    errorMessage,
    isOpen: !!anchorEl,
    openCriteriaTypes,
    openCriterion,
    closeCriteria,
    backToCriteriaTypes,
    selectCriterion: startCriterionDraft,
    changeDraftOperator: setDraftOperator,
    toggleDraftOption,
    applyDraft,
    removeCriterion,
  };
};
